import { useState, useEffect, useCallback, useRef } from "react"
import { useRouter } from "next/router"

// Simple client-side API call using Next.js API route
async function fetchBlogsClient(params = {}) {
  const searchParams = new URLSearchParams();
  
  if (params.page) searchParams.append('page', params.page);
  if (params.pageSize) searchParams.append('pageSize', params.pageSize);
  if (params.search) searchParams.append('search', params.search);
  if (params.category && params.category !== 'All') searchParams.append('category', params.category);
  
  const url = `/api/blogs?${searchParams.toString()}`;
  const response = await fetch(url);

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(`API Error: ${response.status} - ${errorData.message || response.statusText}`);
  }

  return await response.json();
}

// Sort blogs by date (latest first) using available date fields
function sortBlogsByDate(blogs = []) {
  return [...blogs].sort((a, b) => {
    const aDate = new Date(a?.Date || a?.publishedAt || a?.createdAt || 0).getTime()
    const bDate = new Date(b?.Date || b?.publishedAt || b?.createdAt || 0).getTime()
    return bDate - aDate
  })
}

/**
 * Custom hook for managing blog search, filtering, and pagination
 * @param {Object} initialData - Initial blog data from server
 * @param {number} pageSize - Number of blogs per page
 */
export function useBlogSearch(initialData, pageSize = 9) {
  const router = useRouter()
  const [blogs, setBlogs] = useState(() => sortBlogsByDate(initialData?.initialBlogs?.data || []))
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(initialData?.initialBlogs?.meta?.pagination?.pageCount || 1)
  const [totalItems, setTotalItems] = useState(initialData?.initialBlogs?.meta?.pagination?.total || 0)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All")
  const debounceTimeoutRef = useRef(null)
  const isInitializedRef = useRef(false)
  const isUpdatingFromUrlRef = useRef(false)

  // Debounced search function - defined early so it can be used in effects
  const debouncedSearch = useCallback((search, category, page) => {

    // Don't trigger search on initial load if no search parameters
    if (!isInitializedRef.current && !search && category === "All" && page === 1) {
      return
    }
    
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current)
    }
    
    debounceTimeoutRef.current = setTimeout(async () => {
      setIsLoading(true)
      try {
        const params = {
          search: search || undefined,
          category: category !== 'All' ? category : undefined,
          page: page,
          pageSize: pageSize
        }
        
        const response = await fetchBlogsClient(params)
        
        if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
          setBlogs(sortBlogsByDate(response.data))
          setTotalPages(response.meta?.pagination?.pageCount || 1)
          setTotalItems(response.meta?.pagination?.total || 0)
          
          // Smooth scroll to top when new data is loaded
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else if (response?.data && Array.isArray(response.data) && response.data.length === 0) {
          setBlogs([])
          setTotalPages(1)
          setTotalItems(0)
          
          // Smooth scroll to top when no results found
          window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
          // Fallback to client-side filtering if API fails or returns unexpected structure
          let filteredBlogs = sortBlogsByDate(initialData?.initialBlogs?.data || [])

          if (search) {
            filteredBlogs = filteredBlogs.filter(
              (blog) =>
                blog.title.toLowerCase().includes(search.toLowerCase()) ||
                blog.Description.toLowerCase().includes(search.toLowerCase())
            )
          }

          if (category !== "All") {
            filteredBlogs = filteredBlogs.filter((blog) => 
              blog.categories && blog.categories.some((cat) => cat.name === category)
            )
          }

          // Apply pagination to filtered results
          const startIndex = (page - 1) * pageSize
          const endIndex = startIndex + pageSize
          const paginatedBlogs = sortBlogsByDate(filteredBlogs).slice(startIndex, endIndex)

          setBlogs(paginatedBlogs)
          setTotalPages(Math.ceil(filteredBlogs.length / pageSize))
          setTotalItems(filteredBlogs.length)
          
          // Smooth scroll to top for client-side filtering
          window.scrollTo({ top: 0, behavior: 'smooth' })
        }
      } catch (error) {
        // console.error("❌ Error fetching blogs:", error)
        // Fallback to client-side filtering when API fails
        let filteredBlogs = sortBlogsByDate(initialData?.initialBlogs?.data || [])

        if (search) {
          filteredBlogs = filteredBlogs.filter(
            (blog) =>
              blog.title.toLowerCase().includes(search.toLowerCase()) ||
              blog.Description.toLowerCase().includes(search.toLowerCase())
          )
        }

        if (category !== "All") {
          filteredBlogs = filteredBlogs.filter((blog) => 
            blog.categories && blog.categories.some((cat) => cat.name === category)
          )
        }

        // Apply pagination to filtered results
        const startIndex = (page - 1) * pageSize
        const endIndex = startIndex + pageSize
        const paginatedBlogs = sortBlogsByDate(filteredBlogs).slice(startIndex, endIndex)

        setBlogs(paginatedBlogs)
        setTotalPages(Math.ceil(filteredBlogs.length / pageSize))
        setTotalItems(filteredBlogs.length)
        
        // Smooth scroll to top for error fallback
        window.scrollTo({ top: 0, behavior: 'smooth' })
      } finally {
        setIsLoading(false)
      }
    }, 300)
  }, [initialData, pageSize])

  // Initialize state from URL parameters on mount and when user navigates (back/forward)
  useEffect(() => {
    const { page, search, category } = router.query
    
    // Update state from URL (this handles both initial load and back/forward navigation)
    const newPage = page ? Number(page) : 1
    const newSearch = search || ""
    const newCategory = category || "All"
    
    // Mark that we're updating from URL to prevent the URL update effect from running
    isUpdatingFromUrlRef.current = true
    
    setCurrentPage(newPage)
    setSearchTerm(newSearch)
    setSelectedCategory(newCategory)
    
    // If already initialized, this is a back/forward navigation - fetch data
    if (isInitializedRef.current) {
      debouncedSearch(newSearch, newCategory, newPage)
    }
    
    // Mark as initialized after first run
    isInitializedRef.current = true
    
    // Reset the flag after a brief delay to allow state updates to complete
    setTimeout(() => {
      isUpdatingFromUrlRef.current = false
    }, 50)
  }, [router.query, debouncedSearch])

  // Update URL when search parameters change (from user actions, not back/forward navigation)
  useEffect(() => {
    // Skip if not initialized or if we're updating from URL
    if (!isInitializedRef.current || isUpdatingFromUrlRef.current) {
      return
    }

    const params = new URLSearchParams()
    if (searchTerm) params.set('search', searchTerm)
    if (selectedCategory !== 'All') params.set('category', selectedCategory)
    if (currentPage > 1) params.set('page', currentPage.toString())

    const queryString = params.toString()
    const newUrl = queryString ? `/blogs?${queryString}` : '/blogs'
    
    // Only update URL if it's different from current
    // Use push() to create new history entries for back button support
    if (router.asPath !== newUrl) {
      router.push(newUrl, undefined, { shallow: true })
    }
  }, [searchTerm, selectedCategory, currentPage, router])

  // Handle search input
  const handleSearch = useCallback((e) => {
    const value = e.target.value
    setSearchTerm(value)
    setCurrentPage(1)
    debouncedSearch(value, selectedCategory, 1)
    
    // Smooth scroll to top when search changes
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [selectedCategory, debouncedSearch])

  // Handle category selection
  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category)
    setCurrentPage(1)
    debouncedSearch(searchTerm, category, 1)
    
    // Smooth scroll to top when category changes
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [searchTerm, debouncedSearch])

  // Handle page change
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page)
    debouncedSearch(searchTerm, selectedCategory, page)
    
    // Smooth scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [searchTerm, selectedCategory, debouncedSearch])

  // Get current query parameters for URL updates
  const getQueryParams = useCallback(() => {
    const params = {}
    if (searchTerm) params.search = searchTerm
    if (selectedCategory !== "All") params.category = selectedCategory
    return params
  }, [searchTerm, selectedCategory])

  return {
    blogs,
    isLoading,
    currentPage,
    totalPages,
    totalItems,
    searchTerm,
    selectedCategory,
    handleSearch,
    handleCategorySelect,
    handlePageChange,
    getQueryParams
  }
}
